import React, { useState, useEffect, useMemo } from 'react'

import useSignUpValidation from 'components/Auth/AuthForms/hooks/useSignUpValidation'
import { isEmpty } from 'helpers/lodash'
import { getQueryParams } from 'helpers/urls'
import { validateFullName } from 'helpers/user'
import useCountdownTimer from 'hooks/useCountdownTimer'

import * as SignupFormStyles from './styles'

import useSignupOtp from '../hooks/useSignupOtp'
import * as AuthFormStyles from '../styles'

const numberRegex = /^\d+$/

const SignupForm = () => {
    const [phoneNumber, setPhoneNumber] = useState('')
    const [parentPhoneNumber, setParentPhoneNumber] = useState('')
    const [isOtpInputActive, setIsOtpInputActive] = useState(false)
    const [name, setName] = useState({ firstName: '', lastName: '' })

    const [otp, setOtp] = useState('')
    const [sameAsParent, setSameAsParent] = useState(false)

    const {
        isParentPhoneValid,
        isPhoneValid,
        isOTPValid,
    } = useSignUpValidation(parentPhoneNumber, phoneNumber, otp)
    const { sendOtp, otpSending, verifyOtp, otpVerifying } = useSignupOtp()
    const { timeLeft, timerActive, startCountdown } = useCountdownTimer(30)

    const parentNumberInputRef = React.useRef(null)
    const numberInputRef = React.useRef(null)
    const otpButtonRef = React.useRef(null)
    const loginButtonRef = React.useRef(null)

    useEffect(() => {
        otpSending && startCountdown()
        // eslint-disable-next-line
    }, [otpSending])

    const onNameChange = (e, type) => {
        const value = e.target.value
        setName(prevName => ({ ...prevName, [type]: value }))
    }

    const handleNameInputKeyPress = e => {
        if (e.key === 'Enter') {
            parentNumberInputRef.current.focus()
        }
    }

    const onParentMobileChange = e => {
        const value = e.target.value
        if (isEmpty(value) || (numberRegex.test(value) && value.length <= 10)) {
            setParentPhoneNumber(value)
            if (sameAsParent) {
                setPhoneNumber(value)
            }
        }
    }

    const handleParentMobileInputKeyPress = e => {
        if (e.key === 'Enter' && parentNumberInputRef?.current) {
            numberInputRef.current.focus()
        }
    }

    const onMobileChange = e => {
        const value = e.target.value
        if (isEmpty(value) || (numberRegex.test(value) && value.length <= 10)) {
            setPhoneNumber(value)
        }
    }

    const handleMobileInputKeyPress = e => {
        if (e.key === 'Enter' && otpButtonRef?.current) {
            otpButtonRef.current.click()
        }
    }

    const onOTPChange = e => {
        const value = e.target.value
        if (isEmpty(value) || (numberRegex.test(value) && value.length <= 6)) {
            setOtp(value)
        }
    }

    const handleOTPInputKeyPress = e => {
        if (e.key === 'Enter' && loginButtonRef?.current) {
            loginButtonRef.current.click()
        }
    }

    const onCheckboxChange = e => {
        setSameAsParent(e.target.checked)
        if (e.target.checked && parentPhoneNumber.length > 0) {
            setPhoneNumber(parentPhoneNumber)
        }
    }

    const redirectUrl = useMemo(() => {
        let redirect = '/login'
        const { next } = getQueryParams()
        if (!isEmpty(next)) redirect += `?next=${next}`
        return redirect
    }, [])

    const isParentPhoneValidAndNotEmpty =
        !isParentPhoneValid && !!parentPhoneNumber.length

    const isPhoneValidAndNotEmpty = !isPhoneValid && !!phoneNumber.length

    const hasFullName = validateFullName(name)

    const isFormIncomplete =
        !hasFullName || !isPhoneValid || !isParentPhoneValid

    const shouldDisableSendOtpBtn = isFormIncomplete || timerActive

    const studentNumberInformation = (
        <SignupFormStyles.InfoWrapper>
            <SignupFormStyles.InfoIcon />
            <SignupFormStyles.Tooltip visible={phoneNumber?.length}>
                OTP will be sent here
            </SignupFormStyles.Tooltip>
        </SignupFormStyles.InfoWrapper>
    )

    return (
        <AuthFormStyles.Wrapper>
            <AuthFormStyles.Title>
                <AuthFormStyles.Orange>oh. </AuthFormStyles.Orange>
                tell us about yourself
            </AuthFormStyles.Title>
            <AuthFormStyles.Spam>
                this information is just for us
            </AuthFormStyles.Spam>
            <AuthFormStyles.FormWrapper>
                <AuthFormStyles.MobileInput
                    onKeyDown={handleNameInputKeyPress}
                    value={name.firstName}
                    onChange={e => onNameChange(e, 'firstName')}
                    placeholder="enter first name"
                />

                <AuthFormStyles.MobileInput
                    onKeyDown={handleNameInputKeyPress}
                    value={name.lastName}
                    onChange={e => onNameChange(e, 'lastName')}
                    placeholder="enter last name"
                />
                <AuthFormStyles.MobileInput
                    ref={parentNumberInputRef}
                    invalid={isParentPhoneValidAndNotEmpty}
                    onChange={onParentMobileChange}
                    onKeyDown={handleParentMobileInputKeyPress}
                    value={parentPhoneNumber}
                    placeholder="parent number"
                    pattern="[0-9]*"
                    type="tel"
                />
                {isParentPhoneValidAndNotEmpty && (
                    <AuthFormStyles.MobileInputValidation invalid={true}>
                        please use 10 digit valid mobile number
                    </AuthFormStyles.MobileInputValidation>
                )}
                <AuthFormStyles.MobileNumberBox>
                    <SignupFormStyles.Col>
                        <SignupFormStyles.MobileInputWrapper className="mob-wrapper">
                            <SignupFormStyles.MobileInput
                                disabled={sameAsParent}
                                ref={numberInputRef}
                                invalid={isPhoneValidAndNotEmpty}
                                onChange={onMobileChange}
                                onKeyDown={handleMobileInputKeyPress}
                                value={phoneNumber}
                                placeholder="student number"
                                pattern="[0-9]*"
                                type="tel"
                            />
                            {studentNumberInformation}
                        </SignupFormStyles.MobileInputWrapper>
                        {isPhoneValidAndNotEmpty && (
                            <AuthFormStyles.MobileInputValidation
                                invalid={true}
                            >
                                please use 10 digit valid mobile number
                            </AuthFormStyles.MobileInputValidation>
                        )}
                        <SignupFormStyles.SameAsParentLabel
                            hasError={isPhoneValidAndNotEmpty}
                        >
                            <SignupFormStyles.SameAsParentCheckbox
                                onChange={onCheckboxChange}
                                type="checkbox"
                                checked={sameAsParent}
                            />
                            same as parent number
                            <SignupFormStyles.SameAsParentSpan />
                        </SignupFormStyles.SameAsParentLabel>
                    </SignupFormStyles.Col>
                    <AuthFormStyles.OtpButton
                        ref={otpButtonRef}
                        isDisabled={shouldDisableSendOtpBtn}
                        onClick={async () => {
                            if (otpSending) return
                            setIsOtpInputActive(
                                await sendOtp(
                                    name,
                                    phoneNumber,
                                    parentPhoneNumber
                                )
                            )
                        }}
                    >
                        {timerActive ? `resend ${timeLeft}` : 'send OTP'}
                    </AuthFormStyles.OtpButton>
                </AuthFormStyles.MobileNumberBox>
                {isOtpInputActive && (
                    <>
                        <AuthFormStyles.OtpInput
                            invalid={!isOTPValid && otp.length !== 0}
                            disabled={!isOtpInputActive}
                            onChange={onOTPChange}
                            onKeyDown={handleOTPInputKeyPress}
                            value={otp}
                            placeholder="enter otp"
                            pattern="[0-9]*"
                            type="tel"
                        />
                        {!isOTPValid && otp.length !== 0 && (
                            <AuthFormStyles.OTPValidation invalid={true}>
                                please use 6 digit OTP which you have received
                            </AuthFormStyles.OTPValidation>
                        )}
                        <AuthFormStyles.LoginButton
                            ref={loginButtonRef}
                            isDisabled={
                                !isOTPValid || !isPhoneValid || otpVerifying
                            }
                            onClick={() => verifyOtp(phoneNumber, otp)}
                        >
                            {otpVerifying ? 'getting you in...' : 'sign up'}
                        </AuthFormStyles.LoginButton>
                    </>
                )}
                <AuthFormStyles.Signup>
                    already a registered user??
                    <AuthFormStyles.StyledLink to={redirectUrl}>
                        <AuthFormStyles.SignupLink>
                            log in
                            <AuthFormStyles.RightArrow />
                        </AuthFormStyles.SignupLink>
                    </AuthFormStyles.StyledLink>
                </AuthFormStyles.Signup>
            </AuthFormStyles.FormWrapper>
        </AuthFormStyles.Wrapper>
    )
}

export default SignupForm
